import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './PriceDeviationTable.module.css';
import {
  IngredientAvailabilityDeviationRow,
  IngredientStoreAvailability,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

interface AvailabilityDeviationsTableProps {
  data: IngredientAvailabilityDeviationRow[];
  firstColumnHeader?: string;
  deviationsByStoreHeader?: string;
}

export const AvailabilityDeviationsTable = ({
  data,
  firstColumnHeader,
  deviationsByStoreHeader,
}: AvailabilityDeviationsTableProps) => {
  const { t } = useTranslation();
  const productsHeaderLabel = firstColumnHeader ?? t('priceDeviations.table.modifier');
  const byStoreHeader = deviationsByStoreHeader ?? t('priceDeviations.table.availabilityByStore');

  const sortedStores =
    data.length > 0 ? data[0].storeAvailabilities.map((a: IngredientStoreAvailability) => a.storeNumber) : [];

  return (
    <div className={styles.tableScroll} data-testid="availability-deviations-table-scroll">
      <table className={styles.table}>
        <thead>
          <tr>
            <th rowSpan={2} className={styles.productsHeader}>
              {productsHeaderLabel}
            </th>
            <th colSpan={Math.max(sortedStores.length, 1)} className={styles.deviationsHeader}>
              {byStoreHeader}
            </th>
          </tr>
          <tr>
            {sortedStores.map((store: string) => (
              <td className={styles.storeHeader} key={store}>
                {store}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((product: IngredientAvailabilityDeviationRow, index: number) => (
            <tr className={classNames({ [styles.evenRow]: index % 2 === 0 })} key={product.productName}>
              <td>
                <div>
                  <div className={styles.productName}>{product.productName}</div>
                  {product.productDescription ? (
                    <div className={styles.productDescription}>{product.productDescription}</div>
                  ) : null}
                </div>
              </td>
              {product.storeAvailabilities.map((availability: IngredientStoreAvailability, count: number) => (
                <td
                  className={classNames(styles.price, { [styles.firstPrice]: count === 0 })}
                  key={availability.storeNumber}
                >
                  {availability.isActive ? (
                    <span
                      className={styles.availabilityFilledCircle}
                      data-testid="availability-active"
                      aria-label="Active"
                    />
                  ) : (
                    <span className={styles.availabilityDash} data-testid="availability-inactive" aria-label="Inactive">
                      –
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
